import { useCallback, useEffect, useRef, useState } from "react";
import { nowMs } from "@/hooks/useWatchPartyChat";

const SMALL_S = 0.3;
const LARGE_S = 2;
const NUDGE_MIN = 0.05;
const NUDGE_MAX = 0.1;
const HEARTBEAT_MS = 2000;
const CHECK_MS = 500;
const SEEK_BATCH_MS = 150;
const QUIET_MS = 800;
const HOST_STALE_MS = 8000;
const RESTORE_WAIT_MS = 2500;

const whenReady = (v, fn) => {
  if (v.readyState >= 1) fn();
  else v.addEventListener("loadedmetadata", fn, { once: true });
};

// Host-led playback sync over the Watch Party socket, with per-viewer clock/latency compensation.
export default function usePlaybackSync({ video, isHost, live, lastSync, hostOnline, sendSync, serverNow }) {
  const [detached, setDetached] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [drift, setDrift] = useState(0);
  const quietUntil = useRef(0);
  const hostReady = useRef(false);
  const forceJump = useRef(true); // join / reconnect / tap / re-sync → snap exactly to host
  const syncRef = useRef(lastSync);
  const detachedRef = useRef(false);
  syncRef.current = lastSync;
  detachedRef.current = detached;

  const quiet = () => { quietUntil.current = performance.now() + QUIET_MS; };

  // Host position *now*: position was true at server time `at`; serverNow() cancels both legs of network delay.
  const expectedPos = useCallback((s) => {
    if (!s.playing) return s.position;
    const sNow = serverNow?.();
    const elapsedMs = sNow != null && s.at ? sNow - s.at : nowMs() - s.receivedAt;
    return s.position + Math.max(0, elapsedMs) / 1000;
  }, [serverNow]);

  const hostAway = useCallback((s) => !s || (s.playing && (hostOnline === false || s.host_fresh === false
    || nowMs() - s.receivedAt > HOST_STALE_MS)), [hostOnline]);

  const reconcile = useCallback(() => {
    const v = video;
    const s = syncRef.current;
    if (isHost || !v || !s || detachedRef.current || v.readyState < 1) return;
    if (hostAway(s)) { v.playbackRate = 1; return; }
    const target = Math.min(expectedPos(s), (v.duration || Infinity) - 0.25);
    if (s.playing && v.paused) {
      quiet();
      v.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
    } else if (!s.playing && !v.paused) {
      quiet();
      v.pause();
    }
    const d = target - v.currentTime;
    setDrift(d);
    const jump = forceJump.current && Math.abs(d) > SMALL_S;
    if (jump || Math.abs(d) > LARGE_S || (!s.playing && Math.abs(d) > 0.5)) {
      quiet();
      v.currentTime = target;
      v.playbackRate = 1;
    } else if (s.playing && Math.abs(d) > SMALL_S) {
      v.playbackRate = 1 + Math.sign(d) * Math.min(NUDGE_MAX, Math.max(NUDGE_MIN, Math.abs(d) * 0.1));
    } else {
      v.playbackRate = 1;
    }
    if (!v.paused || !s.playing) forceJump.current = false;
  }, [isHost, video, hostAway, expectedPos]);

  // Re-snap after every (re)connect; flag sockets that stay down (e.g. blocked networks)
  const [socketDown, setSocketDown] = useState(false);
  useEffect(() => {
    if (live) { forceJump.current = true; setSocketDown(false); return undefined; }
    const t = setTimeout(() => setSocketDown(true), 10000);
    return () => clearTimeout(t);
  }, [live]);

  // Viewer: act on the newest host event immediately + periodic drift check
  useEffect(() => { reconcile(); }, [lastSync, reconcile]);
  useEffect(() => {
    if (isHost) return undefined;
    const t = setInterval(reconcile, CHECK_MS);
    return () => clearInterval(t);
  }, [isHost, reconcile]);

  // Viewer: a manual pause/seek detaches from the host until "Re-sync"
  useEffect(() => {
    const v = video;
    if (isHost || !v) return undefined;
    const onUser = (e) => {
      const s = syncRef.current;
      if (performance.now() < quietUntil.current || !s || v.ended) return;
      if (e.type === "pause" && s.playing) setDetached(true);
      if (e.type === "seeking" && Math.abs(v.currentTime - expectedPos(s)) > LARGE_S) setDetached(true);
      if (e.type === "play") setNeedsTap(false);
      if (e.type !== "play") v.playbackRate = 1;
    };
    const evs = ["pause", "seeking", "play"];
    evs.forEach((n) => v.addEventListener(n, onUser));
    return () => evs.forEach((n) => v.removeEventListener(n, onUser));
  }, [isHost, video, expectedPos]);

  // Host: restore own position after a reload before broadcasting anything
  useEffect(() => {
    if (!isHost || hostReady.current) return undefined;
    const v = video;
    const s = syncRef.current;
    if (s && v) {
      hostReady.current = true;
      whenReady(v, () => {
        if (v.currentTime < 1) v.currentTime = expectedPos(s);
        if (s.playing) v.play().catch(() => setNeedsTap(true));
      });
      return undefined;
    }
    const t = setTimeout(() => { hostReady.current = true; }, RESTORE_WAIT_MS);
    return () => clearTimeout(t);
  }, [isHost, lastSync, video, expectedPos]);

  // Host: play/pause sent instantly; seeks batched (≤1 per 150ms, final position always sent); 2s heartbeat
  useEffect(() => {
    const v = video;
    if (!isHost || !live || !v) return undefined;
    let lastSent = 0;
    let trailing = null;
    const send = () => {
      if (!hostReady.current) return;
      clearTimeout(trailing);
      trailing = null;
      lastSent = performance.now();
      sendSync({ playing: !v.paused && !v.ended, position: v.currentTime, at: serverNow?.() ?? undefined });
    };
    const batched = () => {
      const wait = SEEK_BATCH_MS - (performance.now() - lastSent);
      if (wait <= 0) send();
      else if (!trailing) trailing = setTimeout(send, wait);
    };
    const instant = ["play", "pause", "ended"];
    instant.forEach((n) => v.addEventListener(n, send));
    v.addEventListener("seeked", batched);
    const onPlay = () => setNeedsTap(false);
    v.addEventListener("play", onPlay);
    send();
    const hb = setInterval(() => { if (!v.paused) send(); else if (performance.now() - lastSent > HEARTBEAT_MS * 2) send(); }, HEARTBEAT_MS);
    return () => {
      instant.forEach((n) => v.removeEventListener(n, send));
      v.removeEventListener("seeked", batched);
      v.removeEventListener("play", onPlay);
      clearTimeout(trailing);
      clearInterval(hb);
    };
  }, [isHost, live, sendSync, serverNow, video]);

  const resync = useCallback(() => {
    detachedRef.current = false;
    forceJump.current = true;
    setDetached(false);
    reconcile();
  }, [reconcile]);

  const tapToJoin = useCallback(() => {
    const v = video;
    if (!v) return;
    quiet();
    forceJump.current = true;
    v.play().then(() => { setNeedsTap(false); reconcile(); }).catch(() => {});
  }, [reconcile, video]);

  let mode = "waiting";
  if (isHost) mode = "host";
  else if (!live) mode = socketDown ? "no-socket" : "reconnecting";
  else if (detached) mode = "detached";
  else if (lastSync && hostAway(lastSync)) mode = "host-away";
  else if (lastSync) mode = Math.abs(drift) > SMALL_S ? "catching-up" : "synced";

  return { mode, needsTap, drift, resync, tapToJoin };
}
